/**
 * Market Compliance & Regional Tax Calculation Module
 * Provides localized ISO-4217 market compliance rules, dynamic regional sales tax & VAT rules,
 * state/province tax lookup tables, prepared food classification rules, and Stripe Tax API integration.
 */

// Global launch: every country is enabled by default. Countries without an
// explicit Stripe Connect payout integration fall back to cash-only ordering
// (allowProviderPayouts: false) until payouts are configured for that market.
export const COUNTRY_LAUNCH_RESTRICTIONS = {
  DEFAULT: { enabled: true, reason: null, orderPaymentMethod: "cash", allowProviderPayouts: false },
  VC: {
    enabled: true,
    currency: "XCD",
    timezone: "America/St_Vincent",
    orderPaymentMethod: "cash",
    allowProviderPayouts: false
  }
};

export const TARGET_MARKET_RULES = {
  // AMERICAS
  US: { currency: "USD", taxRate: 0.08, name: "United States", symbol: "$" },
  CA: { currency: "CAD", taxRate: 0.13, name: "Canada", symbol: "CA$" },
  MX: { currency: "MXN", taxRate: 0.16, name: "Mexico", symbol: "MX$" },
  BR: { currency: "BRL", taxRate: 0.17, name: "Brazil", symbol: "R$" },
  CL: { currency: "CLP", taxRate: 0.19, name: "Chile", symbol: "CLP$" },
  CO: { currency: "COP", taxRate: 0.19, name: "Colombia", symbol: "COP$" },
  PE: { currency: "PEN", taxRate: 0.18, name: "Peru", symbol: "S/" },
  AR: { currency: "ARS", taxRate: 0.21, name: "Argentina", symbol: "ARS$" },

  // EUROPE & UK
  GB: { currency: "GBP", taxRate: 0.20, name: "United Kingdom", symbol: "£" },
  EU: { currency: "EUR", taxRate: 0.20, name: "European Union", symbol: "€" },
  DE: { currency: "EUR", taxRate: 0.19, name: "Germany", symbol: "€" },
  FR: { currency: "EUR", taxRate: 0.20, name: "France", symbol: "€" },
  IT: { currency: "EUR", taxRate: 0.22, name: "Italy", symbol: "€" },
  ES: { currency: "EUR", taxRate: 0.21, name: "Spain", symbol: "€" },
  NL: { currency: "EUR", taxRate: 0.21, name: "Netherlands", symbol: "€" },
  CH: { currency: "CHF", taxRate: 0.081, name: "Switzerland", symbol: "CHF" },
  NO: { currency: "NOK", taxRate: 0.25, name: "Norway", symbol: "kr" },
  SE: { currency: "SEK", taxRate: 0.25, name: "Sweden", symbol: "kr" },
  DK: { currency: "DKK", taxRate: 0.25, name: "Denmark", symbol: "kr." },

  // ASIA & PACIFIC
  JP: { currency: "JPY", taxRate: 0.10, name: "Japan", symbol: "¥" },
  AU: { currency: "AUD", taxRate: 0.10, name: "Australia", symbol: "A$" },
  NZ: { currency: "NZD", taxRate: 0.15, name: "New Zealand", symbol: "NZ$" },
  SG: { currency: "SGD", taxRate: 0.09, name: "Singapore", symbol: "S$" },
  HK: { currency: "HKD", taxRate: 0.00, name: "Hong Kong", symbol: "HK$" },
  IN: { currency: "INR", taxRate: 0.18, name: "India", symbol: "₹" },

  // MIDDLE EAST & AFRICA
  AE: { currency: "AED", taxRate: 0.05, name: "United Arab Emirates", symbol: "AED" },
  SA: { currency: "SAR", taxRate: 0.15, name: "Saudi Arabia", symbol: "SAR" },
  ZA: { currency: "ZAR", taxRate: 0.15, name: "South Africa", symbol: "R" },

  DEFAULT: { currency: "USD", taxRate: 0.00, name: "International", symbol: "$" }
};

// Deprecated: kept only for backward compatibility with older imports.
// Launch is now global (see COUNTRY_LAUNCH_RESTRICTIONS.DEFAULT.enabled), so
// use getSupportedLaunchCountries() instead of this static list.
export const SUPPORTED_LAUNCH_COUNTRIES = Object.keys(TARGET_MARKET_RULES).filter((code) => code !== "DEFAULT");

/**
 * Dynamic US State & Canadian Province Regional Tax Tables
 * Includes state/provincial rates, combined local jurisdiction estimates, and prepared food tax rules.
 */
export const REGIONAL_TAX_TABLES = {
  US: {
    CA: { stateRate: 0.0725, localRate: 0.0160, preparedFoodTaxable: true, name: "California" },
    NY: { stateRate: 0.0400, localRate: 0.0452, preparedFoodTaxable: true, name: "New York" },
    TX: { stateRate: 0.0625, localRate: 0.0195, preparedFoodTaxable: true, name: "Texas" },
    FL: { stateRate: 0.0600, localRate: 0.0105, preparedFoodTaxable: true, name: "Florida" },
    IL: { stateRate: 0.0625, localRate: 0.0257, preparedFoodTaxable: true, name: "Illinois" },
    WA: { stateRate: 0.0650, localRate: 0.0288, preparedFoodTaxable: true, name: "Washington" },
    MA: { stateRate: 0.0625, localRate: 0.0075, preparedFoodTaxable: true, name: "Massachusetts" },
    PA: { stateRate: 0.0600, localRate: 0.0034, preparedFoodTaxable: true, name: "Pennsylvania" },
    GA: { stateRate: 0.0400, localRate: 0.0338, preparedFoodTaxable: true, name: "Georgia" },
    NC: { stateRate: 0.0475, localRate: 0.0224, preparedFoodTaxable: true, name: "North Carolina" },
    OH: { stateRate: 0.0575, localRate: 0.0149, preparedFoodTaxable: true, name: "Ohio" },
    MI: { stateRate: 0.0600, localRate: 0.0000, preparedFoodTaxable: true, name: "Michigan" },
    VA: { stateRate: 0.0530, localRate: 0.0400, preparedFoodTaxable: true, name: "Virginia" },
    CO: { stateRate: 0.0290, localRate: 0.0487, preparedFoodTaxable: true, name: "Colorado" },
    NJ: { stateRate: 0.06625, localRate: 0.0000, preparedFoodTaxable: true, name: "New Jersey" },
    OR: { stateRate: 0.0000, localRate: 0.0000, preparedFoodTaxable: false, name: "Oregon" },
    MT: { stateRate: 0.0000, localRate: 0.0000, preparedFoodTaxable: false, name: "Montana" },
    NH: { stateRate: 0.0000, localRate: 0.0000, preparedFoodTaxable: false, name: "New Hampshire" },
    DE: { stateRate: 0.0000, localRate: 0.0000, preparedFoodTaxable: false, name: "Delaware" },
    AK: { stateRate: 0.0000, localRate: 0.0176, preparedFoodTaxable: false, name: "Alaska" }
  },
  CA: {
    ON: { gst: 0.05, pst: 0.00, hst: 0.13, name: "Ontario" },
    BC: { gst: 0.05, pst: 0.07, hst: 0.00, name: "British Columbia" },
    QC: { gst: 0.05, pst: 0.09975, hst: 0.00, name: "Quebec" },
    AB: { gst: 0.05, pst: 0.00, hst: 0.00, name: "Alberta" },
    NS: { gst: 0.00, pst: 0.00, hst: 0.15, name: "Nova Scotia" },
    NB: { gst: 0.00, pst: 0.00, hst: 0.15, name: "New Brunswick" },
    NL: { gst: 0.00, pst: 0.00, hst: 0.15, name: "Newfoundland and Labrador" },
    PE: { gst: 0.00, pst: 0.00, hst: 0.15, name: "Prince Edward Island" },
    MB: { gst: 0.05, pst: 0.07, hst: 0.00, name: "Manitoba" },
    SK: { gst: 0.05, pst: 0.06, hst: 0.00, name: "Saskatchewan" }
  },
  GB: {
    hotTakeawayRate: 0.20,
    coldTakeawayRate: 0.00,
    dineInRate: 0.20
  },
  EU: {
    DE: { standard: 0.19, takeoutPrepared: 0.07 },
    FR: { standard: 0.20, restaurantMeal: 0.10, packagedFood: 0.055 },
    IT: { standard: 0.22, restaurantMeal: 0.10 },
    ES: { standard: 0.21, restaurantMeal: 0.10 },
    NL: { standard: 0.21, foodReduced: 0.09 }
  }
};

export function getMarketRule(countryCode) {
  if (!countryCode) return TARGET_MARKET_RULES.DEFAULT;
  const code = String(countryCode).toUpperCase();
  return TARGET_MARKET_RULES[code] || TARGET_MARKET_RULES.DEFAULT;
}

export function normalizeCountryCode(countryCode = "US") {
  const normalized = String(countryCode || "US").trim().toUpperCase();
  const countryAliases = {
    "SAINT VINCENT AND THE GRENADINES": "VC",
    "ST. VINCENT AND THE GRENADINES": "VC",
    "ST VINCENT AND THE GRENADINES": "VC"
  };
  return countryAliases[normalized] || normalized;
}

export function isSupportedLaunchCountry(countryCode = "US") {
  const policy = getLaunchCountryPolicy(countryCode);
  return policy.enabled !== false;
}

export function getLaunchCountryPolicy(countryCode = "US") {
  const normalizedCountry = normalizeCountryCode(countryCode);
  return COUNTRY_LAUNCH_RESTRICTIONS[normalizedCountry] || COUNTRY_LAUNCH_RESTRICTIONS.DEFAULT;
}

// ISO-3166 alpha-2 country code -> ITU-T E.164 calling code (no "+").
// Covers every country/territory so normalizePhoneNumber() can correctly
// auto-prefix a bare national number for any country, not just a small
// hardcoded subset. Keep in sync with the copy in app.js.
export const COUNTRY_CALLING_CODES = {
  AF: "93", AL: "355", DZ: "213", AD: "376", AO: "244", AG: "1", AR: "54", AM: "374",
  AU: "61", AT: "43", AZ: "994", BS: "1", BH: "973", BD: "880", BB: "1", BY: "375",
  BE: "32", BZ: "501", BJ: "229", BT: "975", BO: "591", BA: "387", BW: "267", BR: "55",
  BN: "673", BG: "359", BF: "226", BI: "257", KH: "855", CM: "237", CA: "1", CV: "238",
  CF: "236", TD: "235", CL: "56", CN: "86", CO: "57", KM: "269", CG: "242", CD: "243",
  CR: "506", CI: "225", HR: "385", CU: "53", CY: "357", CZ: "420", DK: "45", DJ: "253",
  DM: "1", DO: "1", EC: "593", EG: "20", SV: "503", GQ: "240", ER: "291", EE: "372",
  SZ: "268", ET: "251", FJ: "679", FI: "358", FR: "33", GA: "241", GM: "220", GE: "995",
  DE: "49", GH: "233", GR: "30", GD: "1", GT: "502", GN: "224", GW: "245", GY: "592",
  HT: "509", HN: "504", HK: "852", HU: "36", IS: "354", IN: "91", ID: "62", IR: "98",
  IQ: "964", IE: "353", IL: "972", IT: "39", JM: "1", JP: "81", JO: "962", KZ: "7",
  KE: "254", KI: "686", KP: "850", KR: "82", KW: "965", KG: "996", LA: "856", LV: "371",
  LB: "961", LS: "266", LR: "231", LY: "218", LI: "423", LT: "370", LU: "352", MO: "853",
  MG: "261", MW: "265", MY: "60", MV: "960", ML: "223", MT: "356", MH: "692", MR: "222",
  MU: "230", MX: "52", FM: "691", MD: "373", MC: "377", MN: "976", ME: "382", MA: "212",
  MZ: "258", MM: "95", NA: "264", NR: "674", NP: "977", NL: "31", NZ: "64", NI: "505",
  NE: "227", NG: "234", MK: "389", NO: "47", OM: "968", PK: "92", PW: "680", PA: "507",
  PG: "675", PY: "595", PE: "51", PH: "63", PL: "48", PT: "351", QA: "974", RO: "40",
  RU: "7", RW: "250", KN: "1", LC: "1", VC: "1", WS: "685", SM: "378", ST: "239",
  SA: "966", SN: "221", RS: "381", SC: "248", SL: "232", SG: "65", SK: "421", SI: "386",
  SB: "677", SO: "252", ZA: "27", SS: "211", ES: "34", LK: "94", SD: "249", SR: "597",
  SE: "46", CH: "41", SY: "963", TW: "886", TJ: "992", TZ: "255", TH: "66", TL: "670",
  TG: "228", TO: "676", TT: "1", TN: "216", TR: "90", TM: "993", TV: "688", UG: "256",
  UA: "380", AE: "971", GB: "44", US: "1", UY: "598", UZ: "998", VU: "678", VA: "39",
  VE: "58", VN: "84", YE: "967", ZM: "260", ZW: "263", PS: "970", GL: "299", PR: "1",
  XK: "383"
};

export function normalizePhoneNumber(phone = "", countryCode = "US") {
  if (!phone || typeof phone !== "string") {
    return null;
  }

  const compact = phone.trim();
  if (!compact) {
    return null;
  }

  const plusDigits = compact.startsWith("+") ? compact.replace(/[^\d]/g, "") : "";
  if (plusDigits) {
    const candidatePlus = `+${plusDigits}`;
    return isValidE164Phone(candidatePlus) ? candidatePlus : null;
  }

  let digits = compact.replace(/\D/g, "");
  if (!digits) {
    return null;
  }

  if (digits.startsWith("00") && digits.length > 2) {
    digits = digits.slice(2);
  }

  const normalizedCountry = normalizeCountryCode(countryCode);
  const countryPrefix = COUNTRY_CALLING_CODES[normalizedCountry] || "";
  if (!countryPrefix) {
    const fallback = `+${digits}`;
    return isValidE164Phone(fallback) ? fallback : null;
  }

  if (digits.startsWith("0") && countryPrefix !== "1") {
    digits = digits.replace(/^0+/, "");
  }

  let fullDigits = digits;
  if (countryPrefix === "1" && digits.length === 10) {
    fullDigits = `${countryPrefix}${digits}`;
  } else if (!digits.startsWith(countryPrefix)) {
    fullDigits = `${countryPrefix}${digits}`;
  }

  const normalized = `+${fullDigits}`;
  return isValidE164Phone(normalized) ? normalized : null;
}

export function isValidE164Phone(phone = "") {
  return /^\+[1-9]\d{7,14}$/.test(String(phone || "").trim());
}

export function normalizeProviderTimezone(timezone = "UTC") {
  if (!timezone || typeof timezone !== "string") {
    return "UTC";
  }

  const tz = timezone.trim();
  if (typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function") {
    try {
      const supported = Intl.supportedValuesOf("timeZone");
      if (supported.includes(tz)) {
        return tz;
      }
    } catch (error) {
      // Fall back to a safe timezone default.
    }
  }

  const fallbackMap = {
    EST: "America/New_York",
    CST: "America/Chicago",
    MST: "America/Denver",
    PST: "America/Los_Angeles",
    UTC: "UTC",
    GMT: "Europe/London"
  };

  return fallbackMap[tz.toUpperCase()] || "UTC";
}

function parseTimeRange(range = "") {
  if (!range || typeof range !== "string") {
    return null;
  }

  const parts = range.split("-");
  if (parts.length !== 2) {
    return null;
  }

  const parseTime = (rawTime) => {
    const normalized = rawTime.trim();
    if (!normalized) {
      return null;
    }

    const match = normalized.match(/^(\d{1,2})(?::?(\d{2}))?\s*(AM|PM)?$/i);
    if (!match) {
      return null;
    }

    let hour = Number(match[1]);
    const minute = Number(match[2] || 0);
    const meridiem = (match[3] || "").toUpperCase();

    if (meridiem === "PM" && hour < 12) {
      hour += 12;
    }
    if (meridiem === "AM" && hour === 12) {
      hour = 0;
    }

    return hour * 60 + minute;
  };

  const startMinutes = parseTime(parts[0]);
  const endMinutes = parseTime(parts[1]);

  if (startMinutes === null || endMinutes === null) {
    return null;
  }

  return { startMinutes, endMinutes };
}

export function isProviderOpenNow(provider = {}, now = new Date(), timezoneOverride = null) {
  if (!provider) {
    return false;
  }

  if (provider.isOpenNow === true) {
    return true;
  }

  const timezone = normalizeProviderTimezone(timezoneOverride || provider.timezone || "UTC");
  const localDate = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).formatToParts(now);

  const localMap = {};
  for (const part of localDate) {
    if (part.type !== "literal") {
      localMap[part.type] = part.value;
    }
  }

  const dayKey = localMap.weekday || "Mon";
  const currentMinutes = Number(localMap.hour || 0) * 60 + Number(localMap.minute || 0);
  const schedule = provider.hours || provider.businessHours || provider.openingHours || provider.availability || {};

  const candidateEntries = [];
  const weekdayMap = {
    Mon: "mon",
    Tue: "tue",
    Wed: "wed",
    Thu: "thu",
    Fri: "fri",
    Sat: "sat",
    Sun: "sun"
  };

  if (schedule && typeof schedule === "object") {
    const matchingKey = weekdayMap[dayKey] || dayKey.toLowerCase();
    if (schedule[matchingKey]) {
      candidateEntries.push(schedule[matchingKey]);
    }

    if (Array.isArray(schedule[matchingKey])) {
      candidateEntries.push(...schedule[matchingKey]);
    }

    if (Array.isArray(schedule)) {
      candidateEntries.push(...schedule);
    }
  }

  if (!candidateEntries.length) {
    return false;
  }

  return candidateEntries.some((entry) => {
    if (typeof entry === "string") {
      const parsed = parseTimeRange(entry);
      return parsed && currentMinutes >= parsed.startMinutes && currentMinutes <= parsed.endMinutes;
    }

    if (entry && typeof entry === "object") {
      const start = parseTimeRange(entry.start || entry.open || "");
      const end = parseTimeRange(entry.end || entry.close || "");
      if (start && end) {
        return currentMinutes >= start.startMinutes && currentMinutes <= end.endMinutes;
      }
    }

    return false;
  });
}

export function getSupportedLaunchCountries() {
  return Object.keys(TARGET_MARKET_RULES).filter((code) => code !== "DEFAULT");
}

export function formatCurrency(amount, countryCode = "US", customCurrency = null) {
  const rule = getMarketRule(countryCode);
  const currencyCode = customCurrency || rule.currency;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode
    }).format(amount);
  } catch (e) {
    return `${rule.symbol || "$"}${Number(amount).toFixed(2)} ${currencyCode}`;
  }
}

/**
 * Calculates Dynamic Regional Tax based on provider/client location, state/province,
 * delivery type (pickup vs delivery vs dine_in), and item prepared food classification.
 */
export function calculateDynamicRegionalTax({
  subtotal = 0,
  countryCode = "US",
  state = null,
  city = null,
  postalCode = null,
  deliveryType = "delivery",
  isPreparedFood = true,
  providerLocation = null,
  clientLocation = null
} = {}) {
  const country = String(countryCode || "US").toUpperCase();
  const rule = getMarketRule(country);
  const amount = Number(subtotal) || 0;

  // 1. US State & Local Jurisdiction Tax Calculation
  if (country === "US") {
    const targetState = (state || clientLocation?.state || providerLocation?.state || "").toUpperCase();
    const usTables = REGIONAL_TAX_TABLES.US;

    if (targetState && usTables[targetState]) {
      const stateRule = usTables[targetState];
      let effectiveRate = stateRule.stateRate + stateRule.localRate;

      // Unprepared grocery items in certain states are tax exempt
      if (!isPreparedFood && !stateRule.preparedFoodTaxable) {
        effectiveRate = 0.00;
      }

      // Delivery tax adjustments in specific jurisdictions
      if (deliveryType === "delivery" && ["CA", "CO"].includes(targetState)) {
        effectiveRate += 0.0025; // Delivery fee / local retail delivery fee adjustment
      }

      const tax = Number((amount * effectiveRate).toFixed(2));
      const total = Number((amount + tax).toFixed(2));

      return {
        subtotal: amount,
        taxRate: Number(effectiveRate.toFixed(4)),
        tax,
        total,
        currency: rule.currency,
        formattedTotal: formatCurrency(total, country),
        jurisdiction: `${stateRule.name} (${targetState})`,
        breakdown: {
          stateTax: Number((amount * stateRule.stateRate).toFixed(2)),
          localTax: Number((amount * (effectiveRate - stateRule.stateRate)).toFixed(2))
        },
        calculationMethod: "dynamic_regional_rules"
      };
    }
  }

  // 2. Canadian Provincial Tax Calculation (GST / PST / HST)
  if (country === "CA") {
    const targetProvince = (state || clientLocation?.state || providerLocation?.state || "").toUpperCase();
    const caTables = REGIONAL_TAX_TABLES.CA;

    if (targetProvince && caTables[targetProvince]) {
      const provRule = caTables[targetProvince];
      let effectiveRate = provRule.hst > 0 ? provRule.hst : provRule.gst + provRule.pst;

      // Ontario Prepared Meal Rebate rule: meals under $4 qualify for point-of-sale HST rebate (only 5% GST charged)
      if (targetProvince === "ON" && amount < 4.00 && isPreparedFood) {
        effectiveRate = 0.05;
      }

      const tax = Number((amount * effectiveRate).toFixed(2));
      const total = Number((amount + tax).toFixed(2));

      return {
        subtotal: amount,
        taxRate: Number(effectiveRate.toFixed(4)),
        tax,
        total,
        currency: rule.currency,
        formattedTotal: formatCurrency(total, country),
        jurisdiction: `${provRule.name} (${targetProvince})`,
        breakdown: {
          gst: Number((amount * (provRule.gst || 0)).toFixed(2)),
          pst: Number((amount * (provRule.pst || 0)).toFixed(2)),
          hst: Number((amount * (provRule.hst || 0)).toFixed(2))
        },
        calculationMethod: "dynamic_regional_rules"
      };
    }
  }

  // 3. UK Prepared Food VAT Calculation
  if (country === "GB") {
    let effectiveRate = REGIONAL_TAX_TABLES.GB.hotTakeawayRate;
    if (!isPreparedFood && deliveryType !== "dine_in") {
      effectiveRate = REGIONAL_TAX_TABLES.GB.coldTakeawayRate;
    }

    const tax = Number((amount * effectiveRate).toFixed(2));
    const total = Number((amount + tax).toFixed(2));

    return {
      subtotal: amount,
      taxRate: effectiveRate,
      tax,
      total,
      currency: rule.currency,
      formattedTotal: formatCurrency(total, country),
      jurisdiction: "United Kingdom (HMRC VAT)",
      calculationMethod: "dynamic_regional_rules"
    };
  }

  // 4. EU Prepared Food VAT Calculation
  if (REGIONAL_TAX_TABLES.EU[country]) {
    const euRule = REGIONAL_TAX_TABLES.EU[country];
    const effectiveRate = isPreparedFood ? (euRule.restaurantMeal || euRule.takeoutPrepared || euRule.foodReduced || euRule.standard) : euRule.standard;

    const tax = Number((amount * effectiveRate).toFixed(2));
    const total = Number((amount + tax).toFixed(2));

    return {
      subtotal: amount,
      taxRate: effectiveRate,
      tax,
      total,
      currency: rule.currency,
      formattedTotal: formatCurrency(total, country),
      jurisdiction: `${rule.name} VAT`,
      calculationMethod: "dynamic_regional_rules"
    };
  }

  // Fallback to standard country market rule
  const taxRate = rule.taxRate;
  const tax = Number((amount * taxRate).toFixed(2));
  const total = Number((amount + tax).toFixed(2));

  return {
    subtotal: amount,
    taxRate,
    tax,
    total,
    currency: rule.currency,
    formattedTotal: formatCurrency(total, country),
    jurisdiction: rule.name,
    calculationMethod: "standard_country_default"
  };
}

/**
 * Stripe Tax Real-Time API Integration Helper
 * Queries backend Cloud Function endpoint or Stripe Tax API for dynamic real-time calculation.
 */
export async function calculateOrderTaxWithStripe({
  subtotal,
  countryCode = "US",
  state = null,
  city = null,
  postalCode = null,
  deliveryType = "delivery",
  isPreparedFood = true,
  lineItems = [],
  providerLocation = null,
  clientLocation = null,
  backendUrl = null
} = {}) {
  try {
    if (backendUrl || typeof window !== "undefined") {
      const endpoint = backendUrl || "/calculateOrderTaxCallable";
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subtotal,
          countryCode,
          state,
          city,
          postalCode,
          deliveryType,
          isPreparedFood,
          lineItems,
          providerLocation,
          clientLocation
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.tax !== undefined) {
          return data;
        }
      }
    }
  } catch (err) {
    console.warn("[MarketCompliance] Stripe Tax API request failed, using dynamic regional rules engine:", err);
  }

  return calculateDynamicRegionalTax({
    subtotal,
    countryCode,
    state,
    city,
    postalCode,
    deliveryType,
    isPreparedFood,
    providerLocation,
    clientLocation
  });
}

/**
 * Primary Order Tax Calculation Interface
 * Backwards compatible with calculateOrderTax(subtotal, countryCode, customTaxRate)
 * while supporting rich location and prepared food options object as 4th param or options object.
 */
export function calculateOrderTax(subtotal, countryCode, customTaxRate = null, options = {}) {
  if (customTaxRate !== null && typeof customTaxRate === "number") {
    const rule = getMarketRule(countryCode);
    const tax = Number((subtotal * customTaxRate).toFixed(2));
    const total = Number((subtotal + tax).toFixed(2));

    return {
      subtotal: Number(subtotal),
      taxRate: customTaxRate,
      tax,
      total,
      currency: rule.currency,
      formattedTotal: formatCurrency(total, countryCode),
      calculationMethod: "custom_override"
    };
  }

  let opts = options;
  if (customTaxRate !== null && typeof customTaxRate === "object") {
    opts = customTaxRate;
  }

  return calculateDynamicRegionalTax({
    subtotal,
    countryCode,
    state: opts.state,
    city: opts.city,
    postalCode: opts.postalCode,
    deliveryType: opts.deliveryType || "delivery",
    isPreparedFood: opts.isPreparedFood !== undefined ? opts.isPreparedFood : true,
    providerLocation: opts.providerLocation,
    clientLocation: opts.clientLocation
  });
}

/**
 * Generates a compliant, formal PDF/HTML Tax Receipt for Client Orders.
 * Supports US Sales Tax, CA GST/PST/HST, EU/UK VAT tax invoice compliance.
 */
export function generateTaxReceiptHTML(order = {}) {
  const {
    id = "ORDER-RECEIPT",
    createdAt = new Date().toISOString(),
    clientName = "Valued Customer",
    clientEmail = "",
    clientAddress = "",
    providerName = "What To Eat Provider",
    providerTaxId = "TAX-REG-992014",
    items = [],
    subtotal = 0,
    tax = 0,
    taxRate = 0,
    total = 0,
    currency = "USD",
    countryCode = "US",
    jurisdiction = "Local Tax Jurisdiction",
    taxBreakdown = {}
  } = order;

  const formattedSubtotal = formatCurrency(subtotal, countryCode, currency);
  const formattedTax = formatCurrency(tax, countryCode, currency);
  const formattedTotal = formatCurrency(total, countryCode, currency);

  const itemsHtml = Array.isArray(items) ? items.map(item => `
    <tr style="border-bottom: 1px solid #e2e8f0;">
      <td style="padding: 10px; text-align: left;">${item.name || item.title || "Meal Item"}</td>
      <td style="padding: 10px; text-align: center;">${item.quantity || 1}</td>
      <td style="padding: 10px; text-align: right;">${formatCurrency(item.price || 0, countryCode, currency)}</td>
      <td style="padding: 10px; text-align: right;">${formatCurrency((item.price || 0) * (item.quantity || 1), countryCode, currency)}</td>
    </tr>
  `).join("") : "";

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Tax Receipt - ${id}</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1a202c; padding: 30px; margin: 0; background-color: #f8fafc; }
        .receipt-card { max-width: 650px; margin: 0 auto; background: #ffffff; border-radius: 10px; box-shadow: 0 4px 15px rgba(0,0,0,0.05); padding: 30px; border: 1px solid #e2e8f0; }
        .header { display: flex; justify-content: space-between; border-bottom: 2px solid #ed8936; padding-bottom: 20px; margin-bottom: 20px; }
        .brand { font-size: 22px; font-weight: bold; color: #dd6b20; }
        .receipt-title { font-size: 18px; font-weight: bold; text-align: right; color: #2d3748; }
        .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 25px; font-size: 13px; line-height: 1.6; }
        .meta-box { background: #f7fafc; padding: 12px; border-radius: 6px; border: 1px solid #edf2f7; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 14px; }
        th { background: #edf2f7; padding: 10px; text-align: left; font-weight: 600; color: #4a5568; }
        .totals-table { width: 250px; margin-left: auto; margin-bottom: 25px; }
        .totals-table td { padding: 6px 0; }
        .total-row { font-size: 16px; font-weight: bold; border-top: 2px solid #2d3748; color: #2b6cb0; }
        .footer { font-size: 11px; text-align: center; color: #a0aec0; border-top: 1px solid #e2e8f0; padding-top: 15px; }
      </style>
    </head>
    <body>
      <div class="receipt-card">
        <div class="header">
          <div>
            <div class="brand">What To Eat?</div>
            <div style="font-size: 12px; color: #718096;">Global Meal Ordering Platform</div>
          </div>
          <div>
            <div class="receipt-title">OFFICIAL TAX RECEIPT</div>
            <div style="font-size: 12px; color: #718096; text-align: right;">Receipt #: ${id}</div>
            <div style="font-size: 12px; color: #718096; text-align: right;">Date: ${new Date(createdAt).toLocaleDateString()}</div>
          </div>
        </div>

        <div class="meta-grid">
          <div class="meta-box">
            <strong>Merchant / Provider:</strong><br>
            ${providerName}<br>
            Tax Registration #: <strong>${providerTaxId}</strong><br>
            Jurisdiction: ${jurisdiction}
          </div>
          <div class="meta-box">
            <strong>Billed To:</strong><br>
            ${clientName}<br>
            ${clientEmail ? clientEmail + '<br>' : ''}
            ${clientAddress || 'Global Delivery Customer'}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th style="text-align: center;">Qty</th>
              <th style="text-align: right;">Price</th>
              <th style="text-align: right;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHtml}
          </tbody>
        </table>

        <table class="totals-table">
          <tr>
            <td>Subtotal:</td>
            <td style="text-align: right;">${formattedSubtotal}</td>
          </tr>
          <tr>
            <td>Tax (${(taxRate * 100).toFixed(2)}% ${jurisdiction}):</td>
            <td style="text-align: right;">${formattedTax}</td>
          </tr>
          <tr class="total-row">
            <td style="padding-top: 10px;">Total Paid:</td>
            <td style="padding-top: 10px; text-align: right;">${formattedTotal}</td>
          </tr>
        </table>

        <div class="footer">
          This document serves as an official tax receipt and invoice for goods & prepared food service.<br>
          Generated by What To Eat? Tax Service Engine.
        </div>
      </div>
    </body>
    </html>
  `;
}

export function printTaxReceipt(order = {}) {
  const html = generateTaxReceiptHTML(order);
  const win = window.open("", "_blank", "width=800,height=900");
  if (win) {
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 500);
  }
}
